import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Sun, Moon, User, Shield, Palette, Bell, LogOut, Building2, UserRound, Briefcase } from 'lucide-react';
import { useAuthStore } from '../state/authStore';
import { useThemeStore } from '../state/themeStore';
import { usePortfolioStore } from '../state/portfolioStore';
import { useWatchlistStore } from '../state/watchlistStore';
import { homeForUserType } from '../components/layout/RequireUserType';

export default function SettingsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const updateUser = useAuthStore((s) => s.updateUser);
  const setAccountType = useAuthStore((s) => s.setAccountType);
  const { isDarkMode, toggleTheme } = useThemeStore();
  const brokerConnections = usePortfolioStore((s) => s.brokerConnections);
  const watchlistCount = useWatchlistStore((s) => s.watchlist.length);

  const currentType = user?.userType === 'institutional' ? 'institutional' : 'retail';
  const [selectedType, setSelectedType] = useState(currentType);
  const [savingType, setSavingType] = useState(false);
  const [typeError, setTypeError] = useState('');
  const [typeSuccess, setTypeSuccess] = useState('');

  useEffect(() => {
    setSelectedType(currentType);
  }, [currentType]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleSaveAccountType = async () => {
    if (selectedType === currentType) return;
    setTypeError('');
    setTypeSuccess('');
    setSavingType(true);
    const result = await setAccountType(selectedType);
    setSavingType(false);
    if (!result.success) {
      setTypeError(result.error || 'Could not switch account type. Try again.');
      return;
    }
    setTypeSuccess(
      selectedType === 'institutional'
        ? 'Switched to Institution. Reloading workspace…'
        : 'Switched to Retail. Reloading workspace…'
    );
    // Hard reload routes the user to the correct home and refreshes the
    // sidebar so institutional-only modules (Clients, Committee, Book View,
    // Team Chat, Reports) appear without stale state.
    setTimeout(() => {
      window.location.assign(homeForUserType(selectedType));
    }, 600);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center text-white text-2xl font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h3 className="font-semibold text-lg">{user?.name || 'User'}</h3>
              <p className="text-sm text-gray-500">{user?.email || 'No email'}</p>
              <Badge variant="outline" className="mt-1 text-[10px]">{user?.userType === 'institutional' ? 'Fund Manager' : 'Retail Investor'}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input defaultValue={user?.name} onBlur={(e) => updateUser({ name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input defaultValue={user?.email} disabled className="opacity-60" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workspace / account type */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="w-4 h-4" /> Workspace
          </CardTitle>
          <CardDescription>
            Switch between the Retail product and the Institutional Advisor OS.
            This changes the modules in your sidebar and the home dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {typeError && (
            <Alert className="border-red-800 bg-red-950">
              <AlertDescription className="text-red-300">{typeError}</AlertDescription>
            </Alert>
          )}
          {typeSuccess && (
            <Alert className="border-emerald-800 bg-emerald-950">
              <AlertDescription className="text-emerald-300">{typeSuccess}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setSelectedType('retail')}
              aria-pressed={selectedType === 'retail'}
              className={`rounded-xl border p-4 text-left transition-colors ${
                selectedType === 'retail'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/70'
                  : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
              }`}
            >
              <UserRound className="mb-3 h-6 w-6 text-emerald-500" />
              <p className="font-semibold text-sm">Retail</p>
              <p className="mt-1 text-xs text-gray-500">
                Markets, watchlist, portfolio, AI research.
              </p>
              {currentType === 'retail' && (
                <Badge variant="outline" className="mt-2 text-[10px]">Current</Badge>
              )}
            </button>
            <button
              type="button"
              onClick={() => setSelectedType('institutional')}
              aria-pressed={selectedType === 'institutional'}
              className={`rounded-xl border p-4 text-left transition-colors ${
                selectedType === 'institutional'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/70'
                  : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
              }`}
            >
              <Building2 className="mb-3 h-6 w-6 text-emerald-500" />
              <p className="font-semibold text-sm">Institution</p>
              <p className="mt-1 text-xs text-gray-500">
                Advisor OS: clients, committee, book view, team workflows.
              </p>
              {currentType === 'institutional' && (
                <Badge variant="outline" className="mt-2 text-[10px]">Current</Badge>
              )}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {selectedType === currentType
                ? 'Pick the other workspace to switch.'
                : `Switching to ${selectedType === 'institutional' ? 'Institution' : 'Retail'}.`}
            </p>
            <Button
              onClick={handleSaveAccountType}
              disabled={savingType || selectedType === currentType}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {savingType ? 'Switching…' : 'Switch workspace'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Palette className="w-4 h-4" /> Appearance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Dark Mode</p>
              <p className="text-xs text-gray-500">Toggle between light and dark theme</p>
            </div>
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-gray-400" />
              <Switch checked={isDarkMode} onCheckedChange={toggleTheme} className="data-[state=checked]:bg-emerald-600" />
              <Moon className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Account Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{brokerConnections.filter((b) => b.isConnected).length}</p>
              <p className="text-xs text-gray-500">Connected Brokers</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{watchlistCount}</p>
              <p className="text-xs text-gray-500">Watchlist Items</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{user?.userType === 'institutional' ? 'Fund' : 'Retail'}</p>
              <p className="text-xs text-gray-500">Account Type</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader>
          <CardTitle className="text-base text-red-600">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Sign Out</p>
              <p className="text-xs text-gray-500">Sign out of your account on this device</p>
            </div>
            <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950 gap-2">
              <LogOut className="w-4 h-4" /> Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
